import React from 'react';
import { Badge, BadgeSize, BadgeVariant } from '@usepepper/ui/atoms/badge';
import { createStyles } from '@/contexts/theme-context';
import {
  formatCurrency,
  formatUnitCount,
  getTotalLineItemPrice,
} from '@/utils/money';
import { ItemImage } from '@usepepper/ui/atoms/item-image';
import { ListItem } from '@usepepper/ui/molecules/list-item';
import { OrderItemDataType } from './order-detail-list';
import { Spacing } from '@usepepper/sprout-kit';
import { Typography } from '@usepepper/ui/atoms/typography';
import { View } from 'react-native';

export const OrderLineItem = ({
  item,
  orderCurrencyCode,
}: {
  item: OrderItemDataType;
  orderCurrencyCode: string;
}) => {
  const [styles, theme] = useStyles();

  const itemName = item.restaurant_display_name;
  const quantity = item.order_item_prices?.pack_quantity_at_order ?? 1;
  const unitCount = item.pack.unit_count ?? 1;
  const packUnit = item.pack.unit;
  const currencyCode =
    item.order_item_prices?.currency_code ?? orderCurrencyCode;
  const unitPriceInMicros =
    item.order_item_prices?.unit_price_at_order_micros ?? 0;

  const totalPrice = formatCurrency(
    getTotalLineItemPrice({
      quantity,
      unitCount,
      unitPriceInMicros,
    }) ?? 0,
    currencyCode,
  );

  const imageWithQuantity = (
    <ItemImage
      alt={itemName}
      badge={
        <Badge
          label={quantity.toString()}
          // Font size of the counter on image (To increase prominence)
          size={BadgeSize.MEDIUM}
          variant={BadgeVariant.ELEVATED}
        />
      }
      imageURLs={item.variants_pack.item.photo_url_list ?? []}
      size={Spacing.SPACE_3XLARGE}
    />
  );

  const lineItemSummary = (
    <View style={styles.flexEnd}>
      <Typography color={theme.sk.color.text.primary} variant="paragraph-base">
        {totalPrice}
      </Typography>
      <Typography color={theme.sk.color.text.tertiary} variant="paragraph-md">
        {`${formatUnitCount(unitCount)}x ${formatCurrency(unitPriceInMicros, currencyCode)}`}
      </Typography>
    </View>
  );

  return (
    <ListItem
      leftElement={imageWithQuantity}
      rightElement={lineItemSummary}
      subtitle={packUnit}
      title={itemName}
    />
  );
};

const useStyles = createStyles(() => ({
  flexEnd: {
    alignItems: 'flex-end',
  },
}));
